import React, { useState, useEffect } from 'react';
import { buildStyles, COLORS, injectDashStyles } from '../shared/DashTheme';
import { ThemeProvider, useTheme, Topbar, StatCard, SectionCard, LoadingScreen, EmptyState } from '../shared/DashWidgets';
import Sidebar from '../shared/Sidebar';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

function BarChart({ items, valueKey, labelKey, color, maxVal }) {
  const max = maxVal || Math.max(...items.map(i => i[valueKey] || 0), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {items.map((item, idx) => (
        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 140, fontSize: 12, color: 'rgba(255,255,255,0.75)', textAlign: 'right', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item[labelKey]}
          </div>
          <div style={{ flex: 1, height: 22, background: 'rgba(255,255,255,0.08)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.round((item[valueKey] || 0) / max * 100)}%`, background: color, borderRadius: 4, transition: 'width 1s ease', minWidth: item[valueKey] > 0 ? 4 : 0 }} />
          </div>
          <div style={{ width: 32, fontSize: 12, fontWeight: 700, color: '#fff', flexShrink: 0 }}>{item[valueKey] || 0}</div>
        </div>
      ))}
    </div>
  );
}

function MultiBarChart({ items, labelKey, series }) {
  const max = Math.max(...items.flatMap(i => series.map(s => i[s.key] || 0)), 1);
  return (
    <div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}>
        {series.map(s => (
          <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'rgba(255,255,255,0.75)' }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: s.color }} />
            {s.label}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {items.map((item, idx) => (
          <div key={idx}>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginBottom: 4 }}>{item[labelKey]}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {series.map(s => (
                <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 60, fontSize: 11, color: 'rgba(255,255,255,0.5)', textAlign: 'right', flexShrink: 0 }}>{s.label}</div>
                  <div style={{ flex: 1, height: 14, background: 'rgba(255,255,255,0.08)', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${Math.round((item[s.key] || 0) / max * 100)}%`, background: s.color, borderRadius: 3, transition: 'width 1s ease' }} />
                  </div>
                  <div style={{ width: 28, fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0 }}>{item[s.key] || 0}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MonthlyTrendsChart({ data }) {
  const max = Math.max(...data.flatMap(d => [d.submitted, d.resolved, d.escalated]), 1);
  const series = [
    { key: 'submitted', label: 'Submitted', color: '#6366f1' },
    { key: 'resolved',  label: 'Resolved',  color: '#10b981' },
    { key: 'escalated', label: 'Escalated', color: '#f97316' },
  ];
  return (
    <div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        {series.map(s => (
          <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'rgba(255,255,255,0.75)' }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: s.color }} />
            {s.label}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', height: 160 }}>
        {data.map((d, idx) => (
          <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <div style={{ width: '100%', display: 'flex', gap: 2, alignItems: 'flex-end', height: 130 }}>
              {series.map(s => (
                <div key={s.key} style={{ flex: 1, background: s.color, borderRadius: '3px 3px 0 0', height: `${Math.round((d[s.key] || 0) / max * 100)}%`, minHeight: d[s.key] > 0 ? 4 : 0, transition: 'height 1s ease', position: 'relative' }} title={`${s.label}: ${d[s.key]}`} />
              ))}
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 4 }}>{d.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PerformanceContent() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const s = buildStyles(theme);
  const [tab, setTab] = useState('admins');
  const [perf, setPerf] = useState(null);
  const [trends, setTrends] = useState([]);
  const [depts, setDepts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  React.useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const [trendYear, setTrendYear] = useState('');
  const [availYears, setAvailYears] = useState([]);
  const [trendsLoading, setTrendsLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/higher-authority/performance'),
      api.get('/higher-authority/monthly-trends'),
      api.get('/higher-authority/dept-stats'),
      api.get('/higher-authority/trend-years'),
    ]).then(([p, t, d, y]) => {
      setPerf(p.data);
      setTrends(Array.isArray(t.data) ? t.data : []);
      setDepts(Array.isArray(d.data) ? d.data : []);
      setAvailYears(Array.isArray(y.data) ? y.data : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const loadTrends = (year) => {
    setTrendsLoading(true);
    const params = year ? { year } : {};
    api.get('/higher-authority/monthly-trends', { params })
      .then(r => { setTrends(Array.isArray(r.data) ? r.data : []); })
      .finally(() => setTrendsLoading(false));
  };

  if (loading) return <LoadingScreen />;

  const admins = perf?.admins || [];
  const authorities = perf?.authorities || [];

  const uniComplaints = depts.length === 0 ? null : null;

  const TABS = [
    { key: 'admins',      label: 'Admins' },
    { key: 'authorities', label: 'Authorities' },
    { key: 'departments', label: 'Departments' },
    { key: 'trends',      label: 'Monthly Trends' },
  ];

  const tabStyle = (key) => ({
    padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600,
    cursor: 'pointer', border: 'none', transition: 'all 0.2s ease',
    background: tab === key ? 'rgba(99,102,241,0.35)' : 'rgba(255,255,255,0.08)',
    color: tab === key ? '#fff' : 'rgba(255,255,255,0.6)',
    borderBottom: tab === key ? '2px solid #6366f1' : '2px solid transparent',
  });

  return (
    <div style={s.page}>
      <div style={s.bgGrid} />
      <div style={s.bgOrb1} />
      <div style={s.bgOrb2} />
      <div style={{ ...s.layout }}>
        {!sidebarCollapsed && <Sidebar collapsed={false} onToggle={() => setSidebarCollapsed(true)} mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />}
        <div style={{ ...s.main, marginLeft: isMobile ? 0 : (sidebarCollapsed ? 0 : 248) }}>
          <Topbar
            title='Performance & Analytics'
            subtitle='University-wide performance overview'
            onToggleSidebar={sidebarCollapsed ? () => setSidebarCollapsed(false) : undefined}
            onMobileMenu={() => setMobileOpen(true)}
          />
          <div style={s.content}>

            {/* Summary stats */}
            <div style={{ ...s.grid4, marginBottom: 24 }}>
              <StatCard label='Total Admins'       value={admins.length}       color={COLORS.amber}  />
              <StatCard label='Total Authorities'  value={authorities.length}  color={COLORS.purple} />
              <StatCard label='Departments Tracked' value={depts.length}       color={COLORS.cyan}   />
              <StatCard label='Months Tracked'     value={trends.length}       color={COLORS.green}  />
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
              {TABS.map(t => <button key={t.key} style={tabStyle(t.key)} onClick={() => setTab(t.key)}>{t.label}</button>)}
            </div>

            {/* ADMINS TAB */}
            {tab === 'admins' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <SectionCard title='Admin Verification Performance' subtitle='Verified vs Rejected complaints per admin'>
                  {admins.length === 0 ? <EmptyState icon='🛡️' text='No admins found' /> : (
                    <MultiBarChart items={admins} labelKey='name' series={[
                      { key: 'verified', label: 'Verified',  color: '#10b981' },
                      { key: 'rejected', label: 'Rejected',  color: '#ef4444' },
                      { key: 'pending',  label: 'Pending',   color: '#f59e0b' },
                    ]} />
                  )}
                </SectionCard>
                <SectionCard title='Admin Negligence Count' subtitle='Higher = more ignored complaints'>
                  {admins.length === 0 ? <EmptyState icon='🛡️' text='No admins found' /> : (
                    <BarChart items={admins} valueKey='negligence_count' labelKey='name' color='#ef4444' />
                  )}
                </SectionCard>
                <SectionCard title='Admin Details Table'>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={s.table}>
                      <thead style={s.thead}><tr>
                        <th style={s.th}>Name</th><th style={s.th}>Type</th><th style={s.th}>Dept</th>
                        <th style={s.th}>Verified</th><th style={s.th}>Rejected</th><th style={s.th}>Pending</th><th style={s.th}>Negligence</th>
                      </tr></thead>
                      <tbody>
                        {admins.map(a => (
                          <tr key={a.id} className='ur-row-hover'>
                            <td style={s.tdBold}>{a.name}</td>
                            <td style={s.td}>{a.admin_type}</td>
                            <td style={s.td}>{a.department || '—'}</td>
                            <td style={{ ...s.td, color: '#6ee7b7', fontWeight: 700 }}>{a.verified}</td>
                            <td style={{ ...s.td, color: '#fca5a5', fontWeight: 700 }}>{a.rejected}</td>
                            <td style={{ ...s.td, color: '#fcd34d', fontWeight: 700 }}>{a.pending}</td>
                            <td style={{ ...s.td, color: a.negligence_count > 0 ? '#fca5a5' : 'rgba(255,255,255,0.4)', fontWeight: 700 }}>{a.negligence_count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </SectionCard>
              </div>
            )}

            {/* AUTHORITIES TAB */}
            {tab === 'authorities' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <SectionCard title='Authority Resolution Performance' subtitle='Resolved vs Assigned vs Overdue per authority'>
                  {authorities.length === 0 ? <EmptyState icon='⚖️' text='No authorities found' /> : (
                    <MultiBarChart items={authorities} labelKey='name' series={[
                      { key: 'resolved',  label: 'Resolved',  color: '#10b981' },
                      { key: 'assigned',  label: 'Assigned',  color: '#6366f1' },
                      { key: 'overdue',   label: 'Overdue',   color: '#ef4444' },
                      { key: 'escalated', label: 'Escalated', color: '#f97316' },
                    ]} />
                  )}
                </SectionCard>
                <SectionCard title='Authority Negligence Count' subtitle='Higher = more overdue/escalated complaints ignored'>
                  {authorities.length === 0 ? <EmptyState icon='⚖️' text='No authorities found' /> : (
                    <BarChart items={authorities} valueKey='negligence_count' labelKey='name' color='#ef4444' />
                  )}
                </SectionCard>
                <SectionCard title='Authority Details Table'>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={s.table}>
                      <thead style={s.thead}><tr>
                        <th style={s.th}>Name</th><th style={s.th}>Type</th><th style={s.th}>Dept</th>
                        <th style={s.th}>Resolved</th><th style={s.th}>Assigned</th><th style={s.th}>Overdue</th><th style={s.th}>Escalated</th><th style={s.th}>Negligence</th>
                      </tr></thead>
                      <tbody>
                        {authorities.map(a => (
                          <tr key={a.id} className='ur-row-hover'>
                            <td style={s.tdBold}>{a.name}</td>
                            <td style={s.td}>{a.authority_type}</td>
                            <td style={s.td}>{a.department || '—'}</td>
                            <td style={{ ...s.td, color: '#6ee7b7', fontWeight: 700 }}>{a.resolved}</td>
                            <td style={{ ...s.td, color: '#a5b4fc', fontWeight: 700 }}>{a.assigned}</td>
                            <td style={{ ...s.td, color: a.overdue > 0 ? '#fca5a5' : 'rgba(255,255,255,0.4)', fontWeight: 700 }}>{a.overdue}</td>
                            <td style={{ ...s.td, color: a.escalated > 0 ? '#fdba74' : 'rgba(255,255,255,0.4)', fontWeight: 700 }}>{a.escalated}</td>
                            <td style={{ ...s.td, color: a.negligence_count > 0 ? '#fca5a5' : 'rgba(255,255,255,0.4)', fontWeight: 700 }}>{a.negligence_count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </SectionCard>
              </div>
            )}

            {/* DEPARTMENTS TAB */}
            {tab === 'departments' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <SectionCard title='Department Complaint Volume' subtitle='Total complaints per department'>
                  {depts.length === 0 ? <EmptyState icon='🏛️' text='No department data' /> : (
                    <BarChart items={depts.filter(d => d.total > 0)} valueKey='total' labelKey='department' color='#6366f1' />
                  )}
                </SectionCard>
                <SectionCard title='Department Resolution Breakdown' subtitle='Resolved / Pending / Escalated per department'>
                  {depts.filter(d => d.total > 0).length === 0 ? <EmptyState icon='📊' text='No data yet' /> : (
                    <MultiBarChart items={depts.filter(d => d.total > 0)} labelKey='department' series={[
                      { key: 'resolved',  label: 'Resolved',  color: '#10b981' },
                      { key: 'pending',   label: 'Pending',   color: '#f59e0b' },
                      { key: 'escalated', label: 'Escalated', color: '#f97316' },
                    ]} />
                  )}
                </SectionCard>
                <SectionCard title='Resolution Rate by Department' subtitle='% of complaints resolved'>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {depts.filter(d => d.total > 0).map((d, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 160, fontSize: 12, color: 'rgba(255,255,255,0.75)', textAlign: 'right', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.department}</div>
                        <div style={{ flex: 1, height: 18, background: 'rgba(255,255,255,0.08)', borderRadius: 4, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${d.resolution_rate}%`, background: d.resolution_rate >= 70 ? '#10b981' : d.resolution_rate >= 40 ? '#f59e0b' : '#ef4444', borderRadius: 4, transition: 'width 1s ease' }} />
                        </div>
                        <div style={{ width: 44, fontSize: 12, fontWeight: 700, color: '#fff', flexShrink: 0 }}>{d.resolution_rate}%</div>
                      </div>
                    ))}
                    {depts.filter(d => d.total > 0).length === 0 && <EmptyState icon='📊' text='No department complaints yet' />}
                  </div>
                </SectionCard>
              </div>
            )}

            {/* MONTHLY TRENDS TAB */}
            {tab === 'trends' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <SectionCard
                  title='Monthly Complaint Trends'
                  subtitle='All 12 months for a selected year, or last 6 months by default'
                  action={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <select
                        value={trendYear}
                        onChange={e => { setTrendYear(e.target.value); loadTrends(e.target.value || null); }}
                        style={{ ...s.select, width: 'auto', padding: '6px 12px', fontSize: 13 }}
                      >
                        <option value=''>Last 6 Months</option>
                        {availYears.map(y => <option key={y} value={y}>{y} (Full Year)</option>)}
                      </select>
                      {trendsLoading && <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>Loading...</span>}
                    </div>
                  }
                >
                  {trends.length === 0 ? <EmptyState icon='📈' text='No trend data' /> : <MonthlyTrendsChart data={trends} />}
                </SectionCard>
                <SectionCard title='Monthly Breakdown Table'>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={s.table}>
                      <thead style={s.thead}><tr>
                        <th style={s.th}>Month</th><th style={s.th}>Submitted</th><th style={s.th}>Resolved</th><th style={s.th}>Escalated</th><th style={s.th}>Resolution Rate</th>
                      </tr></thead>
                      <tbody>
                        {trends.map((t, i) => {
                          const rate = t.submitted > 0 ? Math.round(t.resolved / t.submitted * 100) : 0;
                          return (
                            <tr key={i} className='ur-row-hover'>
                              <td style={s.tdBold}>{t.label} {t.year}</td>
                              <td style={{ ...s.td, color: '#a5b4fc', fontWeight: 700 }}>{t.submitted}</td>
                              <td style={{ ...s.td, color: '#6ee7b7', fontWeight: 700 }}>{t.resolved}</td>
                              <td style={{ ...s.td, color: t.escalated > 0 ? '#fdba74' : 'rgba(255,255,255,0.4)', fontWeight: 700 }}>{t.escalated}</td>
                              <td style={{ ...s.td, color: rate >= 70 ? '#6ee7b7' : rate >= 40 ? '#fcd34d' : '#fca5a5', fontWeight: 700 }}>{rate}%</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </SectionCard>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}

export default function PerformanceAnalytics() {
  return <ThemeProvider><PerformanceContent /></ThemeProvider>;
}
